import axiosInstance from "./axiosInstance";

// ─────────────────────────────────────────────────────────────────────────────
// BoxPay Test Utility
//
// Available on ANY environment (local / test server) via browser console.
// Never runs in production (guarded by NODE_ENV check in index.js).
//
// Usage — open browser console and type:
//
//   BoxPayTest.help()                              → list all scenarios
//   BoxPayTest.set('503')                          → fail ALL API calls with 503
//   BoxPayTest.set('503', 'payment-methods')       → fail only APIs whose URL contains 'payment-methods'
//   BoxPayTest.set('timeout', 'sessions')          → fail only session API with timeout
//   BoxPayTest.current()                           → see active scenario + filter
//   BoxPayTest.clear()                             → back to normal
//
// Always refresh after set() / clear().
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY        = "BOXPAY_TEST_SCENARIO";
const STORAGE_FILTER_KEY = "BOXPAY_TEST_URL_FILTER";

// ── Scenario definitions ─────────────────────────────────────────────────────
const SCENARIOS = {
  // ── Retry scenarios (only affect checkout/sessions fetch) ─────────────────
  retry_fail_first: "Retry: attempt #1 fails → retry succeeds after 2s",
  retry_fail_both:  "Retry: both attempts fail → technicalError shown",
  retry_404:        "Retry: attempt #1 returns 404 → no retry, session invalid",

  // ── Network / browser level errors ────────────────────────────────────────
  network_error:    "Network: generic network error (CORS / no internet heuristic)",
  timeout:          "Network: request timeout (ECONNABORTED)",
  aborted:          "Network: request aborted (ERR_CANCELED)",

  // ── Server error responses ────────────────────────────────────────────────
  400:              "Server: 400 Bad Request",
  401:              "Server: 401 Unauthorized",
  403:              "Server: 403 Forbidden",
  404:              "Server: 404 Not Found",
  409:              "Server: 409 Conflict",
  422:              "Server: 422 Unprocessable Entity (with fieldErrors)",
  429:              "Server: 429 Too Many Requests",
  500:              "Server: 500 Internal Server Error",
  502:              "Server: 502 Bad Gateway",
  503:              "Server: 503 Service Unavailable",
  504:              "Server: 504 Gateway Timeout",
};

// ── Build a fake axios error for server error scenarios ──────────────────────
const buildServerError = (status, config) => {
  const serverMessages = {
    400: "Bad request — invalid payload",
    401: "Unauthorized — token missing or expired",
    403: "Forbidden — access denied",
    404: "Not found — resource does not exist",
    409: "Conflict — duplicate request",
    422: "Validation failed",
    429: "Too many requests — rate limited",
    500: "Internal server error",
    502: "Bad gateway — upstream service down",
    503: "Service unavailable — try again later",
    504: "Gateway timeout — upstream did not respond",
  };

  const error = new Error(`Simulated ${status}`);
  error.config = config;
  error.request = {};
  error.response = {
    status,
    statusText: serverMessages[status] || `HTTP ${status}`,
    data: {
      message: serverMessages[status] || `HTTP ${status}`,
      ...(status === 422 && {
        fieldErrorItems: [
          { message: "Card number is invalid" },
          { message: "CVV is required" },
        ],
      }),
    },
  };
  return error;
};

// ── Build a fake axios error for network scenarios ───────────────────────────
const buildNetworkError = (scenario, config) => {
  const error = new Error(
    scenario === "timeout"  ? "timeout of 0ms exceeded" :
    scenario === "aborted"  ? "Request cancelled"       :
    "Network Error"
  );
  error.config  = config;
  error.request = {};
  if (scenario === "timeout")  error.code = "ECONNABORTED";
  if (scenario === "aborted")  error.code = "ERR_CANCELED";
  return error;
};

// ── Axios request interceptor ────────────────────────────────────────────────
const registerInterceptor = () => {
  axiosInstance.interceptors.request.use((config) => {
    const scenario  = localStorage.getItem(STORAGE_KEY);
    const urlFilter = localStorage.getItem(STORAGE_FILTER_KEY);

    // No scenario set, or it's a retry scenario (handled in the thunk)
    if (!scenario || scenario.startsWith("retry_")) return config;

    // Never intercept the analytics call itself
    if (config.url?.includes("/ui-analytics")) return config;

    // If a URL filter is set, only intercept requests whose URL matches it
    if (urlFilter && !config.url?.includes(urlFilter)) return config;

    console.warn(`[BoxPayTest] Intercepting: ${config.method?.toUpperCase()} ${config.url}`);
    console.warn(`[BoxPayTest] Scenario: "${scenario}"${urlFilter ? ` | filter: "${urlFilter}"` : ""}`);

    const numericStatus = parseInt(scenario, 10);

    if (!isNaN(numericStatus)) {
      return Promise.reject(buildServerError(numericStatus, config));
    }

    if (["network_error", "timeout", "aborted"].includes(scenario)) {
      return Promise.reject(buildNetworkError(scenario, config));
    }

    return config;
  });
};

// ── Public API exposed on window ─────────────────────────────────────────────
const BoxPayTest = {
  // scenario  — any key from SCENARIOS
  // urlFilter — optional string; only requests whose URL contains this are intercepted
  set(scenario, urlFilter = null) {
    if (!SCENARIOS[scenario]) {
      console.error(`[BoxPayTest] Unknown scenario: "${scenario}". Run BoxPayTest.help() to see all options.`);
      return;
    }
    localStorage.setItem(STORAGE_KEY, String(scenario));

    if (urlFilter) {
      localStorage.setItem(STORAGE_FILTER_KEY, urlFilter);
      console.log(`%c[BoxPayTest] Scenario: "${scenario}" | filter: "${urlFilter}"`, "color: orange; font-weight: bold");
      console.log(`%c→ Only requests whose URL contains "${urlFilter}" will be intercepted`, "color: orange");
    } else {
      localStorage.removeItem(STORAGE_FILTER_KEY);
      console.log(`%c[BoxPayTest] Scenario: "${scenario}" | filter: none (all API calls)`, "color: orange; font-weight: bold");
      console.log(`%c→ ${SCENARIOS[scenario]}`, "color: orange");
    }

    console.log("%cRefresh the page to apply.", "color: grey");
  },

  clear() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_FILTER_KEY);
    console.log("%c[BoxPayTest] Cleared — refresh to apply.", "color: green; font-weight: bold");
  },

  current() {
    const active    = localStorage.getItem(STORAGE_KEY);
    const urlFilter = localStorage.getItem(STORAGE_FILTER_KEY);
    if (active) {
      console.log(`%c[BoxPayTest] Scenario : "${active}" → ${SCENARIOS[active] || "unknown"}`, "color: orange; font-weight: bold");
      console.log(`%c[BoxPayTest] URL filter: ${urlFilter ? `"${urlFilter}"` : "none (all API calls)"}`, "color: orange");
    } else {
      console.log("%c[BoxPayTest] No scenario active — running normally.", "color: green");
    }
  },

  help() {
    console.log("%c╔══ BoxPayTest ══════════════════════════════════════════════╗", "color: cyan; font-weight: bold");
    console.log("%c  BoxPayTest.set('<scenario>')              all API calls     ", "color: cyan");
    console.log("%c  BoxPayTest.set('<scenario>', '<filter>')  matching URLs only", "color: cyan");
    console.log("%c  BoxPayTest.clear()                        back to normal    ", "color: cyan");
    console.log("%c  BoxPayTest.current()                      show active setup ", "color: cyan");
    console.log("%c╠══ Examples ════════════════════════════════════════════════╣", "color: cyan; font-weight: bold");
    console.log("%c  BoxPayTest.set('503')                     ", "color: yellow", "→ fail all APIs with 503");
    console.log("%c  BoxPayTest.set('503', 'payment-methods')  ", "color: yellow", "→ fail only payment-methods API");
    console.log("%c  BoxPayTest.set('timeout', 'sessions')     ", "color: yellow", "→ timeout only session fetch");
    console.log("%c╠══ RETRY SCENARIOS ═════════════════════════════════════════╣", "color: cyan; font-weight: bold");
    ["retry_fail_first", "retry_fail_both", "retry_404"].forEach((key) => {
      console.log(`%c  '${key}'`, "color: yellow; font-weight: bold", `→ ${SCENARIOS[key]}`);
    });
    console.log("%c╠══ NETWORK ERRORS ══════════════════════════════════════════╣", "color: cyan; font-weight: bold");
    ["network_error", "timeout", "aborted"].forEach((key) => {
      console.log(`%c  '${key}'`, "color: yellow; font-weight: bold", `→ ${SCENARIOS[key]}`);
    });
    console.log("%c╠══ SERVER ERRORS ═══════════════════════════════════════════╣", "color: cyan; font-weight: bold");
    [400, 401, 403, 404, 409, 422, 429, 500, 502, 503, 504].forEach((code) => {
      console.log(`%c  '${code}'`, "color: yellow; font-weight: bold", `→ ${SCENARIOS[code]}`);
    });
    console.log("%c╚═══════════════════════════════════════════════════════════╝", "color: cyan; font-weight: bold");
  },
};

// ── Init ─────────────────────────────────────────────────────────────────────
export const initTestUtils = () => {
  registerInterceptor();
  window.BoxPayTest = BoxPayTest;

  const active    = localStorage.getItem(STORAGE_KEY);
  const urlFilter = localStorage.getItem(STORAGE_FILTER_KEY);
  if (active) {
    console.log(
      `%c[BoxPayTest] Active → scenario: "${active}"${urlFilter ? ` | filter: "${urlFilter}"` : " | all APIs"}`,
      "color: orange; font-weight: bold"
    );
  } else {
    console.log("%c[BoxPayTest] Ready. Type BoxPayTest.help() to see all test scenarios.", "color: cyan");
  }
};
